
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Contractor } from '../types';

// Sub-components
import { WarRoomHeader } from './war-room/WarRoomHeader';
import { DeepAgentContext } from './war-room/DeepAgentContext';
import { ExecutiveBrief } from './war-room/ExecutiveBrief';
import { ComplianceMatrix } from './war-room/ComplianceMatrix';
import { DocumentIntelligence } from './war-room/DocumentIntelligence';
import { WarRoomLoadingState } from './war-room/WarRoomLoadingState';
import { WarRoomEmptyState } from './war-room/WarRoomEmptyState';
import { AgentStatusCard } from './war-room/AgentStatusCard';

// Agent Framework
import { AgentOrchestrator } from '../lib/agents/AgentOrchestrator';
import { AgentOutput } from '../lib/agents/types';

export default function ContractDetailView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { appUser } = useAuth();

    // Contract Data State
    const [contract, setContract] = useState<any>(location.state?.contract || null);
    const [contractor, setContractor] = useState<Contractor | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasFavorites, setHasFavorites] = useState(true);
    const [savedContracts, setSavedContracts] = useState<any[]>([]); // For Dropdown

    // 1. Load Contractor Data (Critical for AI Context)
    useEffect(() => {
        const fetchContractor = async () => {
            if (!appUser?.contractorId) return;
            try {
                const { getContractor } = await import('../services/contractors');
                const profile = await getContractor(appUser.contractorId);
                setContractor(profile);
                console.log(`[WarRoom] Context Loaded: ${profile?.name} (${profile?.keywords?.length || 0} keywords)`);
            } catch (err) {
                console.error("Failed to fetch contractor context", err);
            }
        };
        fetchContractor();
    }, [appUser?.contractorId]);

    // 2. Load Playlist for Dropdown
    useEffect(() => {
        const loadPlaylist = async () => {
            if (!appUser?.contractorId) return;

            try {
                const { getSavedContracts } = await import('../services/favorites');
                const savedItems = await getSavedContracts(appUser.contractorId);
                console.log(`[WarRoom] Playlist: Found ${savedItems.length} items.`);

                if (savedItems.length > 0) {
                    const playlist = savedItems.map(item => ({
                        ...item,
                        noticeId: item.id,
                        agency: item.agency || 'Unknown Agency',
                        solicitationNumber: item.solicitationNumber || 'N/A'
                    }));

                    setSavedContracts(playlist);

                    if (!contract) {
                        console.log("[WarRoom] Auto-selecting priority contract:", playlist[0].title);
                        setContract(playlist[0]);
                    }
                }
            } catch (e) {
                console.error("Error loading playlist", e);
            }
        };
        loadPlaylist();
    }, [appUser?.contractorId]);



    // Auto-fetch for /command-center or deep link without state
    useEffect(() => {
        if (!contract) {
            const fetchDefault = async () => {
                if (!appUser?.contractorId) {
                    setIsLoading(false);
                    return;
                }

                setIsLoading(true);
                try {
                    const { getSavedContracts } = await import('../services/favorites');
                    const savedItems = await getSavedContracts(appUser.contractorId);

                    if (savedItems.length > 0) {
                        const topItem = savedItems[0];
                        const fullContract = {
                            ...topItem,
                            noticeId: topItem.id,
                            agency: topItem.agency || 'Unknown Agency'
                        };
                        setContract(fullContract);
                    } else {
                        setHasFavorites(false);
                    }
                } catch (e) {
                    console.error("Failed to fetch default contract", e);
                    setHasFavorites(false);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchDefault();
        } else {
            setIsLoading(false);
        }
    }, [contract, appUser?.contractorId]);

    // Deep Research State
    const [isResearching, setIsResearching] = useState(false);
    const [researchResult, setResearchResult] = useState<string | null>(null);
    const [activeLogs, setActiveLogs] = useState<string[]>([]);

    // Agent Orchestration State
    const [agentStates, setAgentStates] = useState<Record<string, { status: 'idle' | 'analyzing' | 'complete' | 'error', output?: AgentOutput }>>({
        'Technical Analyst': { status: 'idle' },
        'Compliance Officer': { status: 'idle' },
        'Pricing Strategist': { status: 'idle' }
    });

    // Document Analysis State
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [documentAnalysis, setDocumentAnalysis] = useState<any>(null);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        setUploadError(null);
        setDocumentAnalysis(null);

        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64String = (reader.result as string).split(',')[1];
                try {
                    const analyzePDF = httpsCallable(functions, 'analyzePDF');
                    const result = await analyzePDF({
                        fileBase64: base64String,
                        filename: file.name
                    });

                    const data = result.data as any;
                    if (data.success) {
                        setDocumentAnalysis(data.analysis);
                    } else {
                        setUploadError("Analysis failed to return structured data.");
                    }
                } catch (err: any) {
                    console.error("PDF Analysis failed", err);
                    setUploadError(err.message || "Failed to analyze document.");
                } finally {
                    setIsUploading(false);
                }
            };
            reader.onerror = () => {
                setUploadError("Failed to read file.");
                setIsUploading(false);
            };
        } catch (err) {
            setUploadError("Unexpected error during upload.");
            setIsUploading(false);
        }
    };

    const handleDeepResearch = async () => {
        if (!appUser?.contractorId) return;

        setIsResearching(true);
        setResearchResult(null);
        setActiveLogs([]);

        // Reset Agent States
        setAgentStates({
            'Technical Analyst': { status: 'analyzing' },
            'Compliance Officer': { status: 'analyzing' },
            'Pricing Strategist': { status: 'analyzing' }
        });

        setActiveLogs([`> Initializing Multi-Agent Orchestrator...`]);

        try {
            const orchestrator = new AgentOrchestrator();

            // Logic: Use context from the actual contractor profile fetched in step 1
            // (userStack is handled inside the orchestrator using contractor.keywords)

            const { summary, agentOutputs, finalRecommendation } = await orchestrator.orchestrate({
                opportunity: contract,
                userProfile: contractor || {}, // Pass full profile
                files: []
            });

            // Update individual agent states
            setAgentStates({
                'Technical Analyst': { status: 'complete', output: agentOutputs['Technical Analyst'] },
                'Compliance Officer': { status: 'complete', output: agentOutputs['Compliance Officer'] },
                'Pricing Strategist': { status: 'complete', output: agentOutputs['Pricing Strategist'] }
            });

            setActiveLogs(prev => [...prev, `> Analysis complete. Final Recommendation: ${finalRecommendation}`]);
            setResearchResult(summary);

            // Trigger the cloud research in background if needed, but for now we rely on the local synthesis
            const analyzeDeepResearch = httpsCallable(functions, 'analyzeDeepResearch');
            analyzeDeepResearch({
                prompt: `Conduct a deep strategic analysis for this government contract.`,
                context: contract
            }).then(result => {
                const data = result.data as any;
                const text = data.result || data.candidates?.[0]?.content?.parts?.[0]?.text;
                if (text) setResearchResult(prev => `${prev}\n\n---\n\n### 🌐 Automated Deep Reconnaissance\n${text}`);
            }).catch(err => {
                console.error("Background deep research failed", err);
            });

        } catch (err: any) {
            console.error("Deep Research Error:", err);
            setAgentStates(prev => {
                const newState = { ...prev };
                Object.keys(newState).forEach(key => {
                    if (newState[key].status === 'analyzing') newState[key].status = 'error';
                });
                return newState;
            });
            setActiveLogs(prev => [...prev, `!! ERROR: Orchestration failure.`]);
        } finally {
            setIsResearching(false);
        }
    };

    if (isLoading) return <WarRoomLoadingState />;
    if (!contract || !hasFavorites) return <WarRoomEmptyState />;

    const agencyName = contract?.agency || 'Unknown Agency';

    // SAFE DATE PARSING
    let daysRemaining = 'N/A';
    if (contract?.responseDeadline) {
        const deadline = new Date(contract.responseDeadline);
        if (!isNaN(deadline.getTime())) {
            const diffTime = deadline.getTime() - new Date().getTime();
            daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24))).toString();
        }
    }

    return (
        <div className="min-h-screen bg-[#050505] relative pb-20">
            <div className="relative z-10 font-sans">
                <WarRoomHeader
                    contract={contract}
                    savedContracts={savedContracts}
                    onContractSelect={(selected) => {
                        setContract(selected);
                        navigate(`/war-room`, { state: { contract: selected } });
                    }}
                    daysRemaining={daysRemaining}
                    agencyName={agencyName}
                />

                <main className="max-w-[1600px] mx-auto px-8 lg:px-12 my-12 space-y-12">
                    <DeepAgentContext
                        isResearching={isResearching}
                        researchResult={researchResult}
                        activeLogs={activeLogs}
                        onRunDeepScan={handleDeepResearch}
                    />

                    {/* Intelligence Hub - Agents Grid */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <div className="w-2 h-6 bg-blue-500 rounded-full" />
                                Intelligence Hub
                            </h3>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Agents Active</span>
                                <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" /> Neural Sync Status: 100%</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <AgentStatusCard
                                name="Technical Analyst"
                                role="Engineering & SOW Specialist"
                                status={agentStates['Technical Analyst'].status}
                                output={agentStates['Technical Analyst'].output}
                            />
                            <AgentStatusCard
                                name="Compliance Officer"
                                role="Socio-Economic & FAR Expert"
                                status={agentStates['Compliance Officer'].status}
                                output={agentStates['Compliance Officer'].output}
                            />
                            <AgentStatusCard
                                name="Pricing Strategist"
                                role="Competitive LOE Estimator"
                                status={agentStates['Pricing Strategist'].status}
                                output={agentStates['Pricing Strategist'].output}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        <div className="space-y-8">
                            <ExecutiveBrief contract={contract} />
                            <ComplianceMatrix contract={contract} />
                        </div>

                        <DocumentIntelligence
                            isUploading={isUploading}
                            uploadError={uploadError}
                            documentAnalysis={documentAnalysis}
                            onFileUpload={handleFileUpload}
                            onResetAnalysis={() => setDocumentAnalysis(null)}
                        />
                    </div>
                </main>
            </div>
        </div>
    );
}
